import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Trash2,
  ChevronRight,
  Mail,
  Clock,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Database,
  UserX,
  Server,
  KeyRound,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/landing/Footer';

const lastUpdated = 'April 9, 2026';

const tableOfContents = [
  { id: 'overview', title: 'Overview' },
  { id: 'your-rights', title: 'Your Rights' },
  { id: 'data-we-delete', title: 'Data We Delete' },
  { id: 'data-we-retain', title: 'Data We Retain' },
  { id: 'how-to-request', title: 'How to Request Deletion' },
  { id: 'processing-timeline', title: 'Processing Timeline' },
  { id: 'team-accounts', title: 'Team & Workspace Accounts' },
  { id: 'third-party-services', title: 'Third-Party Services' },
  { id: 'consequences', title: 'Consequences of Deletion' },
  { id: 'contact', title: 'Contact' },
];

const deletedDataCategories = [
  {
    icon: UserX,
    title: 'Account & Profile Data',
    items: [
      'Name, email address, and password hash',
      'Profile avatar and display name',
      'OAuth identifiers (Google sign-in)',
      'Notification and language preferences',
      'Two-factor authentication settings',
    ],
  },
  {
    icon: Server,
    title: 'Deployments & Instances',
    items: [
      'Instance configurations and channel settings',
      'Cloud provider connection metadata',
      'Deployment logs and status history',
      'Backup snapshots created on your behalf',
      'Health-check and uptime records',
    ],
  },
  {
    icon: KeyRound,
    title: 'Credentials & API Keys',
    items: [
      'Encrypted cloud provider credentials',
      'Encrypted AI provider API keys',
      'Personal access tokens you generated',
      'Webhook secrets',
      'Active sessions and refresh tokens',
    ],
  },
  {
    icon: Database,
    title: 'Platform Activity',
    items: [
      'Notification history',
      'Blog posts and comments you authored',
      'Affiliate links and referral data (if used)',
      'Activity logs scoped to your account',
      'Email verification and password reset tokens',
    ],
  },
];

const retainedData = [
  {
    title: 'Billing & Tax Records',
    desc: 'Stripe transaction records, invoices, and tax documents are retained for up to 7 years as required by financial and tax regulations.',
    duration: '7 years',
  },
  {
    title: 'Stripe Payment Metadata',
    desc: "Stripe customer IDs and payment intent records are subject to Stripe's own data retention policies.",
    duration: 'Per Stripe policy',
  },
  {
    title: 'Security & Audit Logs',
    desc: 'Records related to security incidents, fraud prevention, abuse detection, and authentication failures are retained for legitimate security purposes.',
    duration: 'Up to 12 months',
  },
  {
    title: 'Legal & Compliance Data',
    desc: 'Data required by court orders, regulatory investigations, or active legal disputes.',
    duration: 'As required',
  },
  {
    title: 'Anonymized Analytics',
    desc: 'Aggregated, non-identifiable platform usage data that cannot be traced back to you.',
    duration: 'Indefinite',
  },
];

export function DataDeletionPage() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-20">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50/60 to-orange-50/60" />
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="h-16 w-16 rounded-full gradient-primary flex items-center justify-center mx-auto mb-6">
                <Trash2 className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Data Deletion Policy</h1>
              <p className="text-muted-foreground text-lg">
                Your data, your choice. Learn how to request complete removal of your SetOpenClaw
                account and associated data.
              </p>
              <p className="text-sm text-muted-foreground mt-3">Last updated: {lastUpdated}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16 flex-1">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Table of Contents */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <h3 className="font-semibold mb-4 text-sm uppercase tracking-wider text-muted-foreground">
                    Contents
                  </h3>
                  <nav className="space-y-1">
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full text-left py-1.5"
                      >
                        <ChevronRight className="h-3 w-3 flex-shrink-0" />
                        {item.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Main Content */}
              <div className="lg:col-span-3 space-y-12">
                {/* Quick Info Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Card className="border-orange-200 bg-orange-50/50">
                    <CardContent className="p-4 text-center">
                      <Clock className="h-6 w-6 mx-auto mb-2 text-orange-600" />
                      <p className="text-sm font-semibold">30 Days</p>
                      <p className="text-xs text-muted-foreground">Maximum processing time</p>
                    </CardContent>
                  </Card>
                  <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardContent className="p-4 text-center">
                      <Mail className="h-6 w-6 mx-auto mb-2 text-emerald-600" />
                      <p className="text-sm font-semibold">2 Methods</p>
                      <p className="text-xs text-muted-foreground">In-app or email</p>
                    </CardContent>
                  </Card>
                  <Card className="border-teal-200 bg-teal-50/50">
                    <CardContent className="p-4 text-center">
                      <Shield className="h-6 w-6 mx-auto mb-2 text-teal-600" />
                      <p className="text-sm font-semibold">GDPR & CCPA</p>
                      <p className="text-xs text-muted-foreground">Aligned with major laws</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Overview */}
                <div id="overview">
                  <h2 className="text-2xl font-bold mb-4">Overview</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    SetOpenClaw is a hosted control plane for deploying AI assistant instances. We
                    collect personal data to provide that service, including account information,
                    deployment configurations, encrypted provider credentials, billing records, and
                    communication data.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    This policy explains how you can request the permanent deletion of your
                    personal data, what specific data will be removed, what must be retained for
                    legal reasons, and the timeline for processing your request.
                  </p>
                </div>

                {/* Your Rights */}
                <div id="your-rights">
                  <h2 className="text-2xl font-bold mb-4">Your Rights</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Under applicable data protection laws, you have the following rights regarding
                    your personal data:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { title: 'Right to Erasure (GDPR Art. 17)', desc: 'Request deletion of all your personal data' },
                      { title: 'Right to Data Portability', desc: 'Export your data before deletion' },
                      { title: 'Right to Know (CCPA)', desc: 'Know what personal data we hold about you' },
                      { title: 'Right to Opt-Out', desc: 'Stop data processing without full deletion' },
                    ].map((right) => (
                      <Card key={right.title}>
                        <CardContent className="p-4">
                          <p className="font-semibold text-sm">{right.title}</p>
                          <p className="text-xs text-muted-foreground mt-1">{right.desc}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Data We Delete */}
                <div id="data-we-delete">
                  <h2 className="text-2xl font-bold mb-4">Data We Delete</h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    When your deletion request is processed, the following categories of data are
                    permanently removed from our systems:
                  </p>
                  <div className="space-y-6">
                    {deletedDataCategories.map((category) => (
                      <Card key={category.title}>
                        <CardContent className="p-5">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                              <category.icon className="h-5 w-5 text-emerald-600" />
                            </div>
                            <h3 className="font-semibold">{category.title}</h3>
                          </div>
                          <div className="space-y-2 ml-[52px]">
                            {category.items.map((item) => (
                              <div key={item} className="flex items-start gap-2">
                                <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-muted-foreground">{item}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Data We Retain */}
                <div id="data-we-retain">
                  <h2 className="text-2xl font-bold mb-4">Data We Retain</h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Certain data is retained after deletion for legal, financial, and compliance
                    obligations. All retained data is anonymized where possible.
                  </p>
                  <Card className="border-amber-200 bg-amber-50/30">
                    <CardContent className="p-5 space-y-4">
                      {retainedData.map((item) => (
                        <div key={item.title} className="flex items-start gap-3">
                          <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium text-sm">{item.title}</p>
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full flex-shrink-0">
                                {item.duration}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                {/* How to Request */}
                <div id="how-to-request">
                  <h2 className="text-2xl font-bold mb-4">How to Request Data Deletion</h2>
                  <div className="space-y-4">
                    <Card>
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <span className="h-8 w-8 rounded-full gradient-primary flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                            1
                          </span>
                          <div>
                            <h3 className="font-semibold mb-1">In-App Account Deletion (Recommended)</h3>
                            <p className="text-sm text-muted-foreground">
                              Sign in to SetOpenClaw, then go to <strong>Settings &rarr; Account
                              &rarr; Delete Account</strong>. Confirm with your password. Your
                              account will be deactivated immediately and data deletion will begin
                              within 24 hours.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <span className="h-8 w-8 rounded-full gradient-primary flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                            2
                          </span>
                          <div>
                            <h3 className="font-semibold mb-1">Email Request</h3>
                            <p className="text-sm text-muted-foreground">
                              Email{' '}
                              <a
                                href="mailto:support@setopenclaw.com"
                                className="text-emerald-600 hover:underline font-medium"
                              >
                                support@setopenclaw.com
                              </a>{' '}
                              with the subject "Data Deletion Request". Include your registered
                              email address. We will verify your identity before processing.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Processing Timeline */}
                <div id="processing-timeline">
                  <h2 className="text-2xl font-bold mb-4">Processing Timeline</h2>
                  <div className="relative">
                    <div className="absolute left-[19px] top-8 bottom-8 w-0.5 bg-border" />
                    <div className="space-y-6">
                      {[
                        { time: 'Immediately', title: 'Account Deactivated', desc: 'Your account is signed out and your active subscriptions are cancelled at the end of the current billing period.' },
                        { time: '24-48 hours', title: 'Acknowledgment Sent', desc: 'We confirm receipt of your deletion request via email.' },
                        { time: '1-7 days', title: 'Instance Teardown', desc: 'Deployed instances are removed from your connected cloud providers and credentials are revoked.' },
                        { time: '7-14 days', title: 'Data Purge', desc: 'Account data, configurations, sessions, and personal information are permanently deleted from our systems.' },
                        { time: 'Up to 30 days', title: 'Completion Confirmation', desc: 'You receive a final email confirming all eligible data has been permanently deleted.' },
                      ].map((step, i) => (
                        <div key={i} className="flex gap-4">
                          <div className="relative">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center z-10 relative">
                              <span className="text-xs font-bold text-primary">{i + 1}</span>
                            </div>
                          </div>
                          <div className="flex-1 pb-2">
                            <p className="text-xs font-medium text-primary">{step.time}</p>
                            <p className="font-semibold">{step.title}</p>
                            <p className="text-sm text-muted-foreground">{step.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Team Accounts */}
                <div id="team-accounts">
                  <h2 className="text-2xl font-bold mb-4">Team & Workspace Accounts</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    If you belong to a team workspace, additional considerations apply:
                  </p>
                  <Card>
                    <CardContent className="p-4">
                      <ul className="text-sm text-muted-foreground space-y-1.5">
                        <li>- Removing your personal account does not delete the workspace if other members remain.</li>
                        <li>- Resources you created (instances, API keys, blog posts) inside a team workspace remain owned by the workspace.</li>
                        <li>- To delete an entire workspace, the workspace owner must initiate the request.</li>
                        <li>- Team owners can manage member access from <strong>Settings &rarr; Team</strong>.</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                {/* Third-Party Services */}
                <div id="third-party-services">
                  <h2 className="text-2xl font-bold mb-4">Third-Party Services</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    We share data with the following third-party services and will request deletion
                    or revocation from each as part of your request:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { name: 'Stripe', desc: 'Payment processing', note: 'Subject to Stripe retention policy' },
                      { name: 'AWS SES', desc: 'Transactional email delivery', note: 'Removed from all mailing lists' },
                      { name: 'Cloud Providers', desc: 'AWS, GCP, Azure, Hetzner where instances are deployed', note: 'Instances torn down, credentials revoked' },
                      { name: 'AI Providers', desc: 'Anthropic, OpenAI, Google AI', note: 'API keys revoked' },
                    ].map((service) => (
                      <Card key={service.name}>
                        <CardContent className="p-4">
                          <p className="font-semibold text-sm">{service.name}</p>
                          <p className="text-xs text-muted-foreground">{service.desc}</p>
                          <p className="text-xs text-emerald-600 mt-1">{service.note}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Consequences */}
                <div id="consequences">
                  <h2 className="text-2xl font-bold mb-4">Consequences of Deletion</h2>
                  <Card className="border-red-200 bg-red-50/30">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3 mb-4">
                        <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0" />
                        <div>
                          <p className="font-bold text-red-700">This action is permanent and irreversible</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            Once processed, your data cannot be recovered. Please consider exporting
                            your data first.
                          </p>
                        </div>
                      </div>
                      <ul className="space-y-2 text-sm text-muted-foreground ml-9">
                        <li>- You will permanently lose access to your SetOpenClaw account.</li>
                        <li>- All deployed instances will be torn down on the connected cloud providers.</li>
                        <li>- Any pending backups or snapshots managed by SetOpenClaw will be deleted.</li>
                        <li>- API keys and webhook secrets will be revoked.</li>
                        <li>- Active subscriptions will be cancelled (no refund for the current billing period).</li>
                        <li>- You cannot create a new account with the same email for 30 days.</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                {/* Contact */}
                <div id="contact">
                  <h2 className="text-2xl font-bold mb-4">Contact</h2>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    For questions about this policy, to exercise your data rights, or to check the
                    status of a deletion request:
                  </p>
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-sm">Contact</p>
                          <a
                            href="mailto:support@setopenclaw.com"
                            className="text-sm text-emerald-600 hover:underline"
                          >
                            support@setopenclaw.com
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <p className="text-xs text-muted-foreground mt-6 text-center">
                    This policy is designed to align with the General Data Protection Regulation
                    (GDPR), the California Consumer Privacy Act (CCPA), and other applicable data
                    protection laws. For more information about how we handle your data, please see
                    our{' '}
                    <Link to="/privacy" className="text-emerald-600 hover:underline">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
